/**
 * Inline (rich-text) image helpers.
 *
 * A rich-text image is referenced from HTML by its DURABLE `/media/<id>/`
 * address, never from `media_resources`. That is why it needs its own extract
 * and strip: the destructive save's id-preservation never sees it, and the
 * 24-hour cleanup only spares a row that is either confirmed on upload
 * (`purpose: 'inline'`) or referenced by a saved test. Dropping the `<img>`
 * from the HTML detaches nothing on the server, so removal has to name the id
 * explicitly (AUTHORING-CONTRACT SS4.1, SS4.4).
 *
 * These stay separate from the Writing chart helpers on purpose: a chart is a
 * `<figure class="writing-task-chart">` embedded per task, while an inline
 * image is a bare `<img>` anywhere in a prompt. Coupling them would drag one
 * workstream's markup contract into the other's.
 */

const IMG_TAG = /<img\b[^>]*>/gi

function attribute(tag: string, name: string): string {
  const match = new RegExp(`${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(tag)
  return match ? match[1].trim() : ''
}

/** The media id when a `src` is a durable `/media/<id>/` address, else null. */
export function mediaIdFromSrc(src: string): string | null {
  const match = /\/media\/(\d+)\/?(?:$|[?#])/.exec(src.trim())
  return match ? match[1] : null
}

export interface EmbeddedImage {
  src: string
  alt: string
  /** The media id when the src is a durable `/media/<id>/` address. */
  mediaId: string | null
}

/** Every `<img>` in the HTML, in document order. */
export function embeddedImages(html: string): EmbeddedImage[] {
  return [...(html.match(IMG_TAG) ?? [])].map((tag) => {
    const src = attribute(tag, 'src')
    return { src, alt: attribute(tag, 'alt'), mediaId: mediaIdFromSrc(src) }
  })
}

/**
 * The durable media ids embedded in the HTML, de-duplicated, in first-seen
 * order. This is the list the remove control is built from - only a durable id
 * can be deleted cleanly, so a stray non-media `<img>` is left for the author
 * to delete in the text itself.
 */
export function embeddedImageIds(html: string): string[] {
  const seen = new Set<string>()
  const ids: string[] = []
  for (const image of embeddedImages(html)) {
    if (image.mediaId && !seen.has(image.mediaId)) {
      seen.add(image.mediaId)
      ids.push(image.mediaId)
    }
  }
  return ids
}

/**
 * Removes every `<img>` addressing one media id, taking a wrapping `<figure>`
 * or a lone-child `<p>` with it so the layout does not keep an empty box. The
 * text change must be persisted BEFORE the media row is deleted, or the delete
 * endpoint answers 409 "Referenced media cannot be deleted" while the HTML
 * reference still survives on the server.
 */
export function stripInlineImage(html: string, mediaId: string): string {
  const src = `src=["'][^"']*\\/media\\/${mediaId}\\/?(?:["']|[?#][^"']*["'])`
  const img = `<img\\b[^>]*${src}[^>]*>`
  const figure = new RegExp(`<figure\\b[^>]*>\\s*(?:${img})\\s*(?:<figcaption\\b[^>]*>[\\s\\S]*?<\\/figcaption>\\s*)?<\\/figure>`, 'gi')
  const wrappedParagraph = new RegExp(`<p\\b[^>]*>\\s*(?:${img})\\s*<\\/p>`, 'gi')
  const bare = new RegExp(img, 'gi')
  return html.replace(figure, '').replace(wrappedParagraph, '').replace(bare, '')
}

/**
 * The image files carried by a drop or paste, in order. A screenshot dragged
 * from the OS arrives in `files`; some browsers only expose it through `items`,
 * so both are read and de-duplicated by identity.
 */
export function imageFilesFromTransfer(source: DataTransfer | null | undefined): File[] {
  if (!source) return []
  const collected: File[] = []
  const add = (file: File | null) => {
    if (file && file.type.startsWith('image/') && !collected.includes(file)) collected.push(file)
  }
  for (const file of Array.from(source.files ?? [])) add(file)
  for (const item of Array.from(source.items ?? [])) {
    if (item.kind === 'file') add(item.getAsFile())
  }
  return collected
}

/**
 * Whether a drag MIGHT carry an image file, judged from what a `dragover` event
 * exposes. `dataTransfer.files` is empty until `drop` and `getAsFile` returns
 * null before it, so an image drag can only be recognised here by an item whose
 * kind is `file` (its `type` when the browser fills it in) or by the `Files`
 * marker in `types`. The editor must claim the drag on `dragover` - a file is
 * not a default drop target for a contenteditable, so without this the `drop`
 * never reaches it and the browser navigates to the image instead.
 */
export function transferMayHaveImage(source: DataTransfer | null | undefined): boolean {
  if (!source) return false
  for (const item of Array.from(source.items ?? [])) {
    if (item.kind === 'file' && (item.type === '' || item.type.startsWith('image/'))) return true
  }
  return Array.from(source.types ?? []).includes('Files')
}

/**
 * True when a drop or paste carries an image the browser would otherwise embed
 * as a NON-durable reference - an `<img>` from another page, or a `blob:`/
 * `data:` payload with no `File` behind it. Those never reach the media
 * pipeline, so the drop is blocked and the author is told to use the image
 * control instead of letting a reference that dies in the exam slip in.
 */
export function transferCarriesForeignImage(source: DataTransfer | null | undefined): boolean {
  if (!source) return false
  const html = source.getData?.('text/html') ?? ''
  return htmlCarriesForeignImage(html)
}

/**
 * True when the HTML embeds an `<img>` the runner cannot durably resolve - one
 * whose `src` is not a `/media/<id>/` address (a hotlink copied from another
 * page, a `blob:`/`data:` payload). A durable media image is left alone; it is
 * safe to keep. This is the value-level check `transferCarriesForeignImage`
 * defers to, and the one the paste path uses once the clipboard HTML is in hand:
 * a screenshot pasted with an accompanying `<img>` hotlink (Chrome "Copy image")
 * must not slip a reference that dies in the exam past the durable-upload path.
 */
export function htmlCarriesForeignImage(html: string): boolean {
  return embeddedImages(html).some((image) => image.src.trim() !== '' && image.mediaId === null)
}
